import { NetworkStatus } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import { Box } from "@repo/components/src/ui/box";
import { Center } from "@repo/components/src/ui/center";
import { HStack } from "@repo/components/src/ui/hstack";
import { Spinner } from "@repo/components/src/ui/spinner";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { downloadFile } from "@repo/components/src/utils/downloadFile";
import {
  formatNumber,
  formatPercentage,
} from "@repo/components/src/utils/numbers";
import { backendUrl } from "@repo/graphql/cache/url";
import { CampaignAnalyticFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import { ExportCampaignCommentsDocument } from "@repo/graphql/documents/campaign/mutation.generated";
import { CampaignCommentsDocument } from "@repo/graphql/documents/campaign/query.generated";
import { MontuCommentSentimentChoices } from "@repo/graphql/gql/graphql";
import { Download } from "lucide-react-native";
import React, { useMemo, useState } from "react";
import {
  CommentRow,
  SentimentTabs,
  type SentimentFilter,
} from "./commentParts";

type Props = {
  report: CampaignAnalyticFragmentFragment;
  campaignId: string;
};

// How many comments each page of the `campaignComments` query pulls.
const COMMENTS_PAGE_SIZE = 30;

// Maps the sentiment tab to the backend enum; "all" omits the filter so the
// query returns every comment for the campaign.
const SENTIMENT_BY_FILTER: Record<
  Exclude<SentimentFilter, "all">,
  MontuCommentSentimentChoices
> = {
  pos: MontuCommentSentimentChoices.Positive,
  neu: MontuCommentSentimentChoices.Neutral,
  neg: MontuCommentSentimentChoices.Negative,
};

const CommentsSection = React.memo(({ report, campaignId }: Props) => {
  const analysis = report.commentsAnalysis;
  const [filter, setFilter] = useState<SentimentFilter>("all");

  // Comments are fetched (and filtered by sentiment) server-side and paginated,
  // so a sparse sentiment is never hidden behind a fixed client-side sample.
  const sentiment = filter === "all" ? undefined : SENTIMENT_BY_FILTER[filter];
  const { data, networkStatus, fetchMore } = useQuery(
    CampaignCommentsDocument,
    {
      variables: { campaignId, sentiment, first: COMMENTS_PAGE_SIZE },
      notifyOnNetworkStatusChange: true,
    },
  );

  const connection = data?.post.comments;
  const comments = useMemo(
    () => connection?.edges.map((edge) => edge.node) ?? [],
    [connection],
  );
  const loadedCount = comments.length;
  const filteredTotal = connection?.totalCount ?? 0;
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;

  const loadingComments =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  const loadMore = () => {
    const pageInfo = connection?.pageInfo;
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor || loadingMore) return;
    fetchMore({ variables: { after: pageInfo.endCursor } });
  };

  const [exportComments, { loading: exporting }] = useMutation(
    ExportCampaignCommentsDocument,
    {
      onCompleted: (data) => {
        const file = data.campaign.exportComments.file;
        downloadFile(file.url, "comentarios-campana.xlsx", backendUrl);
      },
    },
  );

  const handleDownload = () => {
    exportComments({ variables: { campaignId } });
  };

  if (!analysis || analysis.totalCount === 0) {
    return (
      <Box className="bg-background-50 border border-outline-100 rounded-2xl px-6 py-8 items-center">
        <Text size="sm" className="text-typography-500 text-center max-w-md">
          Aún no hay comentarios extraídos de las publicaciones. Una vez que la
          integración con Meta esté activa los comentarios aparecerán acá con su
          análisis de sentimiento.
        </Text>
      </Box>
    );
  }

  const total = analysis.totalCount;
  const { positive, neutral, negative } = analysis.distribution;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);

  return (
    <HStack space="md" className="flex-wrap -m-1.5">
      <Box className="w-2/5 p-1.5 min-w-[280px]">
        <Box className="bg-background-50 border border-outline-100 rounded-2xl px-5 py-5 h-full">
          <VStack space="md">
            <Text
              size="2xs"
              className="text-typography-500 uppercase font-semibold"
            >
              Distribución
            </Text>
            <HStack space="md" className="items-center">
              <DonutSentiment
                total={total}
                positive={positive}
                neutral={neutral}
                negative={negative}
              />
              <VStack space="sm" className="flex-1">
                <LegendRow
                  color="bg-success-500"
                  label="Positivos"
                  pct={pct(positive)}
                />
                <LegendRow
                  color="bg-typography-400"
                  label="Neutros"
                  pct={pct(neutral)}
                />
                <LegendRow
                  color="bg-error-500"
                  label="Negativos"
                  pct={pct(negative)}
                />
              </VStack>
            </HStack>
          </VStack>
        </Box>
      </Box>
      <Box className="flex-1 p-1.5 min-w-[360px]">
        <Box className="bg-background-50 border border-outline-100 rounded-2xl px-5 py-5 h-full">
          <VStack space="md">
            <HStack
              className="justify-between items-center flex-wrap"
              space="md"
            >
              <HStack space="sm" className="items-center">
                <Text size="xs" className="text-typography-500">
                  Mostrando {formatNumber(loadedCount)} de{" "}
                  {formatNumber(filteredTotal)} comentarios
                </Text>
              </HStack>
              <HStack space="sm" className="items-center">
                <SentimentTabs value={filter} onChange={setFilter} />
                <LoadingButton
                  size="xs"
                  variant="solid"
                  action="primary"
                  rounded="lg"
                  onPress={handleDownload}
                  loading={exporting}
                  startIcon={Download}
                  loadingText="Cargando ..."
                >
                  Descargar excel
                </LoadingButton>
              </HStack>
            </HStack>
            {loadingComments ? (
              <Center className="py-16">
                <Spinner size="large" />
              </Center>
            ) : comments.length > 0 ? (
              <VStack space="sm" className="max-h-[460px] overflow-y-auto">
                {comments.map((c) => (
                  <CommentRow key={c.id} comment={c} />
                ))}
                {hasNextPage ? (
                  <LoadingButton
                    size="xs"
                    variant="outline"
                    action="secondary"
                    rounded="lg"
                    onPress={loadMore}
                    loading={loadingMore}
                    loadingText="Cargando ..."
                  >
                    Cargar más comentarios
                  </LoadingButton>
                ) : null}
              </VStack>
            ) : (
              <Text size="sm" className="text-typography-500">
                Sin comentarios en esta categoría.
              </Text>
            )}
          </VStack>
        </Box>
      </Box>
    </HStack>
  );
});

CommentsSection.displayName = "OrganicCommentsSection";

const DonutSentiment = ({
  total,
  positive,
  neutral,
  negative,
}: {
  total: number;
  positive: number;
  neutral: number;
  negative: number;
}) => {
  const denom = Math.max(1, positive + neutral + negative);
  const pos = (positive / denom) * 100;
  const neu = (neutral / denom) * 100;
  const radius = 15.915;
  // We render concentric arcs by using dasharray + dashoffset on rotated circles.
  return (
    <Box
      className="relative items-center justify-center"
      style={{ width: 180, height: 180 } as object}
    >
      <svg viewBox="0 0 36 36" width={180} height={180}>
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="#eef0f3"
          strokeWidth="3.2"
        />
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="#10b981"
          strokeWidth="3.2"
          strokeDasharray={`${pos} ${100 - pos}`}
          strokeDashoffset="25"
          transform="rotate(-90 18 18)"
        />
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="#9ca3af"
          strokeWidth="3.2"
          strokeDasharray={`${neu} ${100 - neu}`}
          strokeDashoffset={`${25 - pos}`}
          transform="rotate(-90 18 18)"
        />
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="#ef4444"
          strokeWidth="3.2"
          strokeDasharray={`${(negative / denom) * 100} ${100 - (negative / denom) * 100}`}
          strokeDashoffset={`${25 - pos - neu}`}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <Box className="absolute items-center">
        <Text size="xl" className="font-bold text-typography-900">
          {formatNumber(total)}
        </Text>
        <Text size="2xs" className="text-typography-500">
          comentarios
        </Text>
      </Box>
    </Box>
  );
};

const LegendRow = ({
  color,
  label,
  pct,
}: {
  color: string;
  label: string;
  pct: number;
}) => (
  <HStack space="sm" className="items-center">
    <Box className={`w-2.5 h-2.5 rounded-full ${color}`} />
    <Text size="sm" className="text-typography-700 flex-1">
      {label}
    </Text>
    <Text size="sm" className="text-typography-900 font-bold">
      {formatPercentage(pct / 100, 0)}
    </Text>
  </HStack>
);

export default CommentsSection;
